// Single source of truth for the Expansion date window.
// Mirrors the api_keys/dashboard preset pattern (moment-based, local tz).

import moment from "moment";
import { toDayKey } from "@/views/setting/ApiKeys/dashboard/utils/aggregate";

// Expansion date-window presets (local tz). Today + 7d are first-class for recency-driven triage —
// the activity pulse buckets finer for shorter windows so they stay readable even for high-volume sources.
export const DATE_RANGE_PRESETS = [
  { days: 1,   label_key: "expansion.dateRange.today"   },
  { days: 7,   label_key: "expansion.dateRange.last7"   },
  { days: 30,  label_key: "expansion.dateRange.last30"  },
  { days: 90,  label_key: "expansion.dateRange.last90"  },
  { days: 365, label_key: "expansion.dateRange.last12m" },
];

export const DEFAULT_WINDOW_DAYS = 90;
export const DEFAULT_WINDOW_LABEL_KEY = "expansion.dateRange.last90";

export const MS_PER_DAY = 86400000;
// Widest selectable preset — the fallback span when a range omits explicit dates.
const WIDEST_PRESET_DAYS = DATE_RANGE_PRESETS[DATE_RANGE_PRESETS.length - 1].days;

// Inclusive-of-today window: days=1 → today only, days=7 → today + 6 prior.
export function presetRange(days) {
  return {
    from: toDayKey(moment().subtract(Math.max(0, days - 1), "day")),
    to:   toDayKey(moment()),
  };
}

export function presetLabelKey(window_days) {
  const match = DATE_RANGE_PRESETS.find((p) => p.days === window_days);
  return match ? match.label_key : null;
}

// Resolve a date_range to a human label: preset name, "from → to" for
// custom ranges, default fallback otherwise.
export function formatDateRangeLabel(range, $t) {
  const key = range && presetLabelKey(range.window_days);
  if (key) return $t(key);
  if (range && range.from && range.to) return `${range.from} → ${range.to}`;
  return $t(DEFAULT_WINDOW_LABEL_KEY);
}

// True when the range is the implicit default (preset window, no custom dates).
// Callers use this to decide whether the date chip should render — the default
// window is background state and surfacing it as a removable chip clutters the
// strip on every load.
export function isDefaultDateWindow(range) {
  if (!range) return true;
  return !range.from && !range.to && Number(range.window_days) === DEFAULT_WINDOW_DAYS;
}

// [from, to] as epoch ms, both local — a date-only string parses as UTC and skews the window by the viewer's offset.
// `to` at end-of-day, `from` at its start; falls back to the last
// window_days from the range end. One source so map, verdict and pulse window identically.
export function rangeBounds(range) {
  const r = range || {};
  const toMs = r.to ? new Date(`${r.to}T23:59:59`).getTime() : Date.now();
  const wd = Number(r.window_days) > 0 ? Number(r.window_days) : WIDEST_PRESET_DAYS;
  const fromMs = r.from ? new Date(`${r.from}T00:00:00`).getTime() : toMs - wd * MS_PER_DAY;
  return { fromMs, toMs };
}
